import { useEffect } from "react";
import type { DocumentCitation } from "./citation";
import type { PreviewRow } from "./types";

/** The cell a citation points to in the preview's table. */
export interface PreviewTarget {
  /** The row's number (the "#" column). */
  row: number;
  /** The column's index; null outlines the whole row. */
  column: number | null;
}

/** A column by its name (any case) or 1-based number; null when neither. */
export const previewColumn = (columns: string[], column?: string) => {
  if (!column) return null;
  const named = columns.findIndex(
    (name) => name.trim().toLowerCase() === column.trim().toLowerCase(),
  );
  if (named >= 0) return named;
  const number = Number(column);
  return Number.isInteger(number) && number >= 1 && number <= columns.length
    ? number - 1
    : null;
};

/**
 * Where a citation points in the preview, loading pages until its row is
 * among the rows shown (the preview loads a page at a time).
 */
export const usePreviewTarget = ({
  citation,
  columns,
  rows,
  hasMore,
  loading,
  filtered,
  loadMore,
}: {
  citation: DocumentCitation | null;
  columns: string[];
  rows: PreviewRow[];
  hasMore: boolean;
  loading: boolean;
  filtered: boolean;
  loadMore: () => void;
}): PreviewTarget | null => {
  const row = citation?.row;
  const reached = !row || rows.some((item) => item.number >= row);
  useEffect(() => {
    if (!reached && hasMore && !loading && !filtered) loadMore();
  }, [reached, hasMore, loading, filtered, loadMore]);
  return row ? { row, column: previewColumn(columns, citation?.column) } : null;
};
